import {
  amountInWords,
  canMoveSalesOrder,
  orderFromQuotation,
  orderProgress,
  setSalesOrderStatus,
  type SalesOrderStatus,
  type SyncOp,
} from '@ims/core'
import type {
  Page,
  Repository,
  SalesOrderBoardFilter,
  SalesOrderBoardRow,
  SalesOrderLineRow,
  SalesOrderRow,
} from '@ims/data'
import {
  Badge,
  Button,
  buttonClass,
  Card,
  ConfirmDialog,
  cx,
  EmptyState,
  Field,
  FilterPill,
  FilterPills,
  IconTile,
  Kbd,
  ListFrame,
  ListToolbar,
  PageHeader,
  Pagination,
  RowActions,
  RowNote,
  RowTitle,
  SearchInput,
  Segmented,
  SkeletonDetail,
  SkeletonTable,
  StateSwap,
  Table,
  TD,
  Textarea,
  TH,
  TR,
  useToast,
} from '@ims/ui'
import {
  ArrowLeft,
  Ban,
  Check,
  ClipboardCheck,
  FilePen,
  FileText,
  Pencil,
  Plus,
  Printer,
  CheckCircle2,
  MessageSquare,
  Receipt as ReceiptIcon,
  Send,
  Trash2,
  X,
} from '@ims/ui/icons'
import { useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { useLookups } from '../components.tsx'
import { errorMessage, useLive, useSettings } from '../hooks.ts'
import { dateLabel, dateShort, money, qty, todayIso } from '../lib/format.ts'
import { useListKeys } from '../lib/list-keys.ts'
import { numberingFor } from '../lib/numbering.ts'
import { lineVatLabel, registrationOf, showsVat } from '../lib/registration.ts'
import {
  KIND_WORDS,
  QUOTE_SOON_DAYS,
  addDays,
  countdown,
  quotationSteps,
  salesBoardState,
  salesOrderStanding,
  salesOrderLineRows,
  salesOrderPath,
  salesOrderRow,
  salesOrderState,
  type QuotationStep,
  type SalesOrderKind,
} from '../lib/sales-orders.ts'
import { salesDocView } from '../lib/doc-view.ts'
import { customerStanding } from '../lib/statement.ts'
import { printName } from '../print.tsx'
import { LEDGER_TABLES } from './customer-detail.tsx'
import { BandStat, Meter, PartyCard, Step } from './doc-band.tsx'
import { DocPrintDialog, type DocToPrint } from './print-dialog.tsx'
import { DocPrintMenu, openPrintMenu } from './print-menu.tsx'
import { useCan, useRepo, useWorkspace } from '../workspace.tsx'
import { CANCELLED_ROW, StatusBadge } from './invoices.tsx'
import { SectionTabs } from '../layout/sections.tsx'

const PAGE = 25

type Show = 'open' | 'finished'
type Pill = NonNullable<SalesOrderBoardFilter['state']>
const PILLS: Record<SalesOrderKind, { value: Pill; label: string }[]> = {
  quotation: [
    { value: 'draft', label: 'Draft' },
    { value: 'waiting', label: 'Sent' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'part', label: 'Part billed' },
    { value: 'expired', label: 'Expired' },
  ],
  order: [
    { value: 'draft', label: 'Draft' },
    { value: 'waiting', label: 'Confirmed' },
    { value: 'part', label: 'Part billed' },
  ],
}
const LIST_TABLES = [
  'sales_orders',
  'sales_order_lines',
  'sales_invoices',
  'sales_invoice_lines',
] as const

const LIST_TEXT = {
  quotation: {
    subtitle: 'Prices you have offered. Nothing leaves stock until you make the bill.',
    empty: 'Quote a customer a price, then turn it into a bill when they say yes.',
    finished:
      'Quotations that were billed, declined, cancelled or made into an order are kept here.',
    order: 'Accepted first, then the ones about to run out.',
  },
  order: {
    subtitle: 'What customers have ordered and what is still to be billed.',
    empty: 'Take an order now and bill it when the goods go out.',
    finished: 'Orders that were fully billed, closed or cancelled are kept here.',
    order: 'The soonest expected first, then drafts.',
  },
} as const

const ROW_BUTTON = 'h-[34px] text-[13px]'
const ROW_ICON =
  'size-[34px] border border-border bg-surface text-ink max-sm:size-10! max-sm:flex-none!'
/** The row the arrow keys are on. */
const ROW_ACTIVE = 'sm:bg-sell-wash sm:shadow-[inset_3px_0_0_var(--color-primary)]'

/** Quotations, or sales orders: the same list for both kinds. */
export function SalesOrdersScreen({ kind }: { kind: SalesOrderKind }) {
  const settings = useSettings()
  const navigate = useNavigate()
  const repo = useRepo()
  const toast = useToast()
  const { current } = useWorkspace()
  const canEdit = useCan('staff')
  const words = KIND_WORDS[kind]
  const [params, setParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [active, setActive] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const today = todayIso()
  const counts = useLive(
    (r) =>
      r.salesOrders.counts?.(kind, today, addDays(today, QUOTE_SOON_DAYS)) ?? Promise.resolve(null),
    [kind, today],
    [...LIST_TABLES],
  ).data
  // The split and the pills need the local database; without it every document is listed.
  // Undefined while the counts load: the list waits, so it never flashes every row.
  const split = counts !== null
  const show: Show = params.get('show') === 'finished' ? 'finished' : 'open'
  const asked = params.get('status') as Pill | null
  const pill = show === 'open' && PILLS[kind].some((p) => p.value === asked) ? asked : null
  const go = (next: { show?: Show; pill?: Pill | null }) => {
    const s = next.show ?? show
    const p = next.pill === undefined ? (next.show ? null : pill) : next.pill
    setParams(
      { ...(s === 'finished' ? { show: 'finished' } : {}), ...(p ? { status: p } : {}) },
      { replace: true },
    )
    setOffset(0)
    setActive(null)
  }
  // Each document with what has been billed, so a row can say "Part billed" or "Billed".
  const page = useLive(
    async (r): Promise<Page<SalesOrderBoardRow>> => {
      if (r.salesOrders.board)
        return r.salesOrders.board({
          kind,
          show: split ? show : undefined,
          state: pill ?? undefined,
          search,
          today,
          limit: PAGE,
          offset,
        })
      const found = await r.salesOrders.list({ kind, search, limit: PAGE, offset })
      const details = await Promise.all(found.rows.map((o) => r.salesOrders.detail(o.id)))
      return {
        total: found.total,
        rows: found.rows.map((order, i) => {
          const d = details[i]
          const done = d ? orderProgress(d.lines, d.invoicedByLine) : null
          return {
            order,
            lineCount: d?.lines.length ?? 0,
            orderedMilli: done?.orderedMilli ?? 0,
            billedMilli: Math.min(done?.invoicedMilli ?? 0, done?.orderedMilli ?? 0),
            unbilledPaisa: order.total_paisa,
            madeOrder: false,
          }
        }),
      }
    },
    [kind, split, show, pill, search, offset, today],
    [...LIST_TABLES],
  )
  const rows = page.data?.rows ?? []
  const openCount = counts
    ? counts.draft + counts.waiting + counts.accepted + counts.part + counts.expired
    : 0
  const filtered = !!search || !!pill

  /** A row's document for its print menu. */
  const loadPrint = async (id: string): Promise<DocToPrint> => {
    const d = await repo.salesOrders.detail(id)
    if (!d) throw new Error('This document is no longer here.')
    return salesPrint(repo, d.order, d.lines, settings)
  }
  /** Leaving draft numbers the document, as the button on its own page does. */
  const send = async (order: SalesOrderRow) => {
    if (!settings || !current) return
    setBusy(order.id)
    try {
      const numbering = await numberingFor(repo, settings, words.doc, todayIso())
      await repo.commit(
        setSalesOrderStatus(
          { tenantId: current.tenant_id, now: new Date().toISOString() },
          salesOrderRow(order),
          kind === 'quotation' ? 'sent' : 'confirmed',
          { numbering },
        ).ops,
      )
      toast.success(
        kind === 'quotation' ? 'Quotation numbered and ready to send' : 'Order confirmed',
      )
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(null)
    }
  }

  const listRef = useRef<HTMLDivElement>(null)
  useListKeys({
    ids: rows.map((r) => r.order.id),
    active,
    setActive,
    page: { offset, limit: PAGE, total: page.data?.total ?? 0, setOffset },
    list: listRef,
    open: () => active && navigate(`${words.path}/${active}`),
    keys: { p: () => active && openPrintMenu(active) },
  })

  const summary =
    counts && openCount > 0 ? (
      kind === 'quotation' ? (
        <>
          <b className="font-semibold text-ink">{counts.waiting} waiting for an answer</b>
          {counts.waitingPaisa > 0 && <> worth {money(counts.waitingPaisa, settings)}</>}
          {counts.accepted > 0 && (
            <span className="font-semibold text-success">
              {' '}
              · {counts.accepted} accepted, ready to bill
            </span>
          )}
          {counts.soon > 0 && (
            <span className="font-semibold text-warning">
              {' '}
              · {counts.soon} run{counts.soon === 1 ? 's' : ''} out within {QUOTE_SOON_DAYS} days
            </span>
          )}
        </>
      ) : (
        <>
          <b className="font-semibold text-ink">{counts.waiting + counts.part} to bill</b>
          {counts.unbilledPaisa > 0 && (
            <>
              {' · '}
              <b className="font-semibold text-ink">{money(counts.unbilledPaisa, settings)}</b>{' '}
              still to bill
            </>
          )}
          {counts.draft > 0 && ` · ${counts.draft} draft${counts.draft === 1 ? '' : 's'}`}
        </>
      )
    ) : (
      LIST_TEXT[kind].subtitle
    )

  return (
    <div>
      <PageHeader
        title="Sales"
        subtitle={summary}
        actions={
          canEdit && (
            <Link to={`${words.path}/new`} className={buttonClass()}>
              <Plus className="size-4" aria-hidden />
              New {words.one.toLowerCase()}
            </Link>
          )
        }
      />
      <SectionTabs section="sales" />
      <ListToolbar>
        {counts && (
          <Segmented
            label="Open or finished"
            className="max-sm:w-full"
            value={show}
            onChange={(v) => go({ show: v })}
            options={[
              { value: 'open', label: `Open ${openCount}` },
              { value: 'finished', label: `Finished ${counts.finished}` },
            ]}
          />
        )}
        {counts && show === 'open' && (
          <FilterPills label="Status">
            <FilterPill selected={!pill} count={openCount} onSelect={() => go({ pill: null })}>
              All
            </FilterPill>
            {PILLS[kind].map((p) => (
              <FilterPill
                key={p.value}
                selected={pill === p.value}
                count={counts[p.value]}
                onSelect={() => go({ pill: p.value })}
              >
                {p.label}
              </FilterPill>
            ))}
          </FilterPills>
        )}
        <SearchInput
          listKeys
          className="max-sm:w-full sm:ml-auto sm:w-64"
          value={search}
          onChange={(v) => {
            setSearch(v)
            setOffset(0)
          }}
          placeholder="Number or customer…"
        />
      </ListToolbar>
      <ListFrame ref={listRef}>
        {!page.data || counts === undefined ? (
          <SkeletonTable cols={5} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<FilePen className="size-6" />}
            title={
              filtered
                ? `No ${words.many.toLowerCase()} match`
                : show === 'finished'
                  ? `No finished ${words.many.toLowerCase()} yet`
                  : counts && counts.finished > 0
                    ? `No open ${words.many.toLowerCase()}`
                    : `No ${words.many.toLowerCase()} yet`
            }
            description={
              filtered
                ? 'Try another status or search.'
                : show === 'finished'
                  ? LIST_TEXT[kind].finished
                  : LIST_TEXT[kind].empty
            }
          />
        ) : (
          <>
            <Table stack="cards">
              <thead>
                <tr>
                  <TH>{words.one}</TH>
                  <TH>Date</TH>
                  <TH>Where it stands</TH>
                  <TH>Status</TH>
                  <TH align="right">Total</TH>
                  <TH />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const o = row.order
                  const state = salesBoardState(row, today)
                  const stands = salesOrderStanding(
                    row,
                    today,
                    (d) => dateShort(d, settings),
                    (p) => money(p, settings),
                  )
                  const name = o.number ?? (o.status === 'draft' ? 'Draft' : 'Not numbered yet')
                  const live = ['sent', 'accepted', 'confirmed'].includes(o.status)
                  const billable =
                    live && !row.madeOrder && state.label !== 'Billed' && state.label !== 'Expired'
                  return (
                    <TR
                      key={o.id}
                      onClick={() => navigate(salesOrderPath(o))}
                      className={cx(
                        o.status === 'cancelled' && CANCELLED_ROW,
                        active === o.id && ROW_ACTIVE,
                      )}
                    >
                      <TD className="stack-title">
                        <RowTitle
                          tile={
                            <IconTile area="sell" size="sm">
                              {kind === 'quotation' ? (
                                <FilePen className="size-4" />
                              ) : (
                                <ClipboardCheck className="size-4" />
                              )}
                            </IconTile>
                          }
                          title={
                            <Link
                              to={salesOrderPath(o)}
                              className="hover:underline focus-visible:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {name}
                            </Link>
                          }
                          sub={o.customer_name}
                        />
                      </TD>
                      <TD
                        className="whitespace-nowrap text-muted max-sm:hidden"
                        title={dateLabel(o.doc_date, settings)}
                      >
                        {dateShort(o.doc_date, settings)}
                      </TD>
                      <TD className="stack-r3">
                        <RowNote {...stands} />
                      </TD>
                      <TD className="stack-end stack-r2">
                        <StateSwap state={state.label}>
                          <Badge tone={state.tone}>{state.label}</Badge>
                        </StateSwap>
                      </TD>
                      <TD align="right" className="stack-end stack-r1 whitespace-nowrap font-bold">
                        {money(o.total_paisa, settings)}
                      </TD>
                      <TD className="stack-actions sm:w-px sm:whitespace-nowrap">
                        <RowActions>
                          {o.status === 'draft' && canEdit ? (
                            <Link
                              to={`${salesOrderPath(o)}/edit`}
                              aria-label="Edit"
                              title="Edit"
                              className={cx(
                                'inline-flex items-center justify-center rounded-xl hover:bg-soft',
                                ROW_ICON,
                              )}
                            >
                              <Pencil className="size-4" aria-hidden />
                            </Link>
                          ) : (
                            o.status !== 'draft' &&
                            state.label !== 'Expired' && (
                              <DocPrintMenu
                                id={o.id}
                                load={() => loadPrint(o.id)}
                                label={`Print ${name}`}
                                triggerClassName={ROW_ICON}
                              />
                            )
                          )}
                          {billable && canEdit && (
                            <Link
                              to={`/bill?from=${o.id}`}
                              className={buttonClass({
                                variant: 'soft',
                                size: 'sm',
                                className: ROW_BUTTON,
                              })}
                            >
                              Make a bill
                            </Link>
                          )}
                          {state.label === 'Expired' && canEdit && (
                            <Link
                              to={`${salesOrderPath(o)}/revise`}
                              className={buttonClass({
                                variant: 'secondary',
                                size: 'sm',
                                className: ROW_BUTTON,
                              })}
                            >
                              Revise
                            </Link>
                          )}
                          {o.status === 'draft' && canEdit && (
                            <Button
                              size="sm"
                              variant="soft"
                              className={ROW_BUTTON}
                              aria-label={`${kind === 'quotation' ? 'Send' : 'Confirm'} ${name}`}
                              loading={busy === o.id}
                              onClick={() => void send(o)}
                            >
                              {kind === 'quotation' ? 'Send' : 'Confirm'}
                            </Button>
                          )}
                        </RowActions>
                      </TD>
                    </TR>
                  )
                })}
              </tbody>
            </Table>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-xs text-muted max-sm:px-1">
              <span>
                {split && show === 'open' ? LIST_TEXT[kind].order : 'Newest first.'} A row opens the{' '}
                {words.one.toLowerCase()}.
              </span>
              <span className="ml-auto flex items-center gap-1.5 max-lg:hidden">
                <Kbd>↑</Kbd> <Kbd>↓</Kbd> move <Kbd>Enter</Kbd> open <Kbd>P</Kbd> print
              </span>
            </div>
            <Pagination offset={offset} limit={PAGE} total={page.data.total} onChange={setOffset} />
          </>
        )}
      </ListFrame>
    </div>
  )
}

/** One quotation or sales order: what was agreed, what has been billed, and what to do next. */
/** A quotation or an order ready for Print or save, with what it revises and the customer's phone. */
export async function salesPrint(
  r: Repository,
  order: SalesOrderRow,
  lines: SalesOrderLineRow[],
  settings: ReturnType<typeof useSettings>,
): Promise<DocToPrint> {
  const [source, customer] = await Promise.all([
    order.source_id ? r.get('sales_orders', order.source_id) : null,
    order.customer_id ? r.get('customers', order.customer_id) : null,
  ])
  const reference = source?.number
    ? { label: source.kind === order.kind ? 'Revises' : 'Made from', number: source.number }
    : null
  const words = KIND_WORDS[order.kind]
  return {
    doc: salesDocView({ order, lines, settings, reference, phone: customer?.phone }),
    name: printName(order.kind === 'quotation' ? 'Quotation' : 'Order', order),
    summary: [
      order.number ?? `${words.one} (number on sync)`,
      order.customer_name,
      money(order.total_paisa, settings),
    ]
      .filter(Boolean)
      .join(' · '),
  }
}

export function SalesOrderDetailScreen({ kind }: { kind: SalesOrderKind }) {
  const { id = '' } = useParams()
  const repo = useRepo()
  const settings = useSettings()
  const { current } = useWorkspace()
  const lookups = useLookups()
  const canEdit = useCan('staff')
  const canManage = useCan('manager')
  const toast = useToast()
  const navigate = useNavigate()
  const words = KIND_WORDS[kind]
  const noun = words.one.toLowerCase()
  const [busy, setBusy] = useState(false)
  // A status that needs a reason (cancel, close), or deleting a draft.
  const [ask, setAsk] = useState<null | 'cancelled' | 'closed' | 'delete'>(null)
  const [reason, setReason] = useState('')
  const [printing, setPrinting] = useState<DocToPrint | null>(null)
  const canMoney = canEdit && Boolean(repo.parties)
  const detail = useLive(
    (r) => r.salesOrders.detail(id),
    [id],
    ['sales_orders', 'sales_order_lines', 'sales_invoices', 'sales_invoice_lines'],
  )
  // The document this one revises (or the quotation an order came from), and its revisions.
  const related = useLive(
    async (r) => {
      const sourceId = (await r.get('sales_orders', id))?.source_id
      const [source, later] = await Promise.all([
        sourceId ? r.get('sales_orders', sourceId) : null,
        r.list('sales_orders', { where: { source_id: id }, limit: 20 }),
      ])
      return { source, later: later.rows.filter((o) => !o.deleted_at) }
    },
    [id],
    ['sales_orders'],
  ).data
  // The customer as on their page: phone, PAN and what they owe now.
  const customerId = detail.data?.order.customer_id ?? null
  const customer = useLive(
    async (r) => {
      if (!customerId) return null
      const [row, ledger] = await Promise.all([
        r.get('customers', customerId),
        canMoney && r.parties ? r.parties.customerLedger(customerId) : null,
      ])
      return { row, standing: ledger ? customerStanding(ledger, todayIso()) : null }
    },
    [customerId, canMoney],
    ['customers', ...LEDGER_TABLES],
  ).data

  if (!detail.data || detail.data.order.kind !== kind)
    return detail.loading ? <SkeletonDetail /> : <EmptyState title={`${words.one} not found`} />
  const { order, lines, invoicedByLine, invoices } = detail.data
  const state = salesOrderState(order, todayIso(), { lines, by: invoicedByLine })
  const billedAny = Object.values(invoicedByLine).some((q) => q > 0)
  const open = ['sent', 'accepted', 'confirmed'].includes(order.status)
  const can = (to: SalesOrderStatus) => canEdit && canMoveSalesOrder(order, to)
  const firstStatus: SalesOrderStatus = kind === 'quotation' ? 'sent' : 'confirmed'
  // A quotation that became an order is billed through that order, not twice.
  const madeOrder = related?.later.find((o) => o.kind === 'order' && o.status !== 'cancelled')
  const canOrder = kind === 'quotation' && open && canEdit && !billedAny && !madeOrder
  const canRevise =
    order.status !== 'draft' && order.status !== 'cancelled' && canEdit && !billedAny && !madeOrder
  const canCancel = can('cancelled') && (kind === 'quotation' || canManage)
  // An empty group would still draw its "More" button.
  const hasSecondary = can(firstStatus) || canRevise
  const hasMore = can('closed') || canCancel || (order.status === 'draft' && canEdit)
  const ctx = () => ({ tenantId: current!.tenant_id, now: new Date().toISOString() })
  const today = todayIso()
  const billing = orderProgress(lines, invoicedByLine)
  const billedMilli = Math.min(billing.invoicedMilli, billing.orderedMilli)
  const vat = showsVat(registrationOf(settings), order.tax_paisa)
  const steps =
    kind === 'quotation'
      ? quotationSteps(order, {
          order: Boolean(madeOrder),
          billed: billedAny,
          expired: state.label === 'Expired',
        })
      : null
  const when = order.valid_until ?? order.expected_date
  const time = when
    ? countdown(order.doc_date, when, today, order.valid_until ? 'left' : 'due')
    : null
  // Counting down only matters while the document is still waiting on it.
  const counting =
    time &&
    (kind === 'quotation'
      ? order.status === 'sent' || order.status === 'draft'
      : open && billing.progress !== 'full' && billing.progress !== 'over')
  const openPrint = async () => setPrinting(await salesPrint(repo, order, lines, settings))

  const run = async (ops: () => Promise<SyncOp[]> | SyncOp[], success: string) => {
    setBusy(true)
    try {
      await repo.commit(await ops())
      toast.success(success)
      setAsk(null)
      setReason('')
      return true
    } catch (error) {
      toast.error(errorMessage(error))
      return false
    } finally {
      setBusy(false)
    }
  }
  const move = (to: SalesOrderStatus, success: string, why?: string) =>
    run(async () => {
      // Leaving draft numbers the document, like saving it does.
      const numbering =
        order.status === 'draft' && settings
          ? await numberingFor(repo, settings, words.doc, todayIso())
          : undefined
      return setSalesOrderStatus(ctx(), salesOrderRow(order), to, { reason: why, numbering }).ops
    }, success)

  /** The customer said yes, the goods go later: an order for what was quoted. */
  const makeOrder = async () => {
    if (!settings) return
    setBusy(true)
    try {
      const today = todayIso()
      const made = orderFromQuotation(ctx(), salesOrderRow(order), salesOrderLineRows(lines), {
        status: 'confirmed',
        docDate: today,
        locationId: order.location_id ?? lookups.activeLocations[0]?.id ?? null,
        roundOff: 'nearest_rupee',
        numbering: await numberingFor(repo, settings, 'sales_order', today),
      })
      await repo.commit(made.ops)
      toast.success('Order made from the quotation')
      navigate(salesOrderPath(made.order))
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <Link
        to={words.path}
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" /> {words.many}
      </Link>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="whitespace-nowrap">
              {order.number ??
                (order.status === 'draft' ? `Draft ${noun}` : `${words.one} (number on sync)`)}
            </span>
            <StateSwap state={state.label}>
              <Badge tone={state.tone}>{state.label}</Badge>
            </StateSwap>
          </span>
        }
        subtitle={[dateLabel(order.doc_date, settings), order.customer_name]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <>
            <Button
              variant="secondary"
              icon={<Printer className="size-4" />}
              onClick={() => void openPrint()}
            >
              Print or save
            </Button>
            {order.status === 'draft' && canEdit && (
              <Link to={`${salesOrderPath(order)}/edit`} className={buttonClass()}>
                <Pencil className="size-4" aria-hidden />
                Edit
              </Link>
            )}
            {canOrder && (
              <Button
                variant="secondary"
                icon={<ClipboardCheck className="size-4" />}
                disabled={busy}
                onClick={() => void makeOrder()}
              >
                Make an order
              </Button>
            )}
            {/* Once everything on it is billed there is nothing left to offer. */}
            {open && canEdit && !madeOrder && state.label !== 'Billed' && (
              <Link to={`/bill?from=${order.id}`} className={buttonClass()}>
                <ReceiptIcon className="size-4" aria-hidden />
                Make a bill
              </Link>
            )}
          </>
        }
        secondary={
          hasSecondary && (
            <>
              {can(firstStatus) && (
                <Button
                  variant="secondary"
                  icon={<Check className="size-4" />}
                  loading={busy}
                  onClick={() =>
                    void move(
                      firstStatus,
                      kind === 'quotation'
                        ? 'Quotation numbered and ready to send'
                        : 'Order confirmed',
                    )
                  }
                >
                  {kind === 'quotation' ? 'Mark as sent' : 'Confirm order'}
                </Button>
              )}
              {canRevise && (
                <Link
                  to={`${salesOrderPath(order)}/revise`}
                  className={buttonClass({ variant: 'secondary' })}
                >
                  <FilePen className="size-4" aria-hidden />
                  Revise
                </Link>
              )}
            </>
          )
        }
        more={
          hasMore && (
            <>
              {can('closed') && (
                <Button
                  variant="ghost"
                  icon={<Check className="size-4" />}
                  onClick={() => setAsk('closed')}
                >
                  Close {noun}
                </Button>
              )}
              {canCancel && (
                <Button
                  variant="ghost"
                  className="text-danger hover:text-danger"
                  icon={<Ban className="size-4" />}
                  onClick={() => setAsk('cancelled')}
                >
                  Cancel {noun}
                </Button>
              )}
              {order.status === 'draft' && canEdit && (
                <Button
                  variant="ghost"
                  className="text-danger hover:text-danger"
                  icon={<Trash2 className="size-4" />}
                  onClick={() => setAsk('delete')}
                >
                  Delete draft
                </Button>
              )}
            </>
          )
        }
      />

      {/* Four numbers first (boards DocQuote, DocQuotePhone): two to a row on a phone. */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <BandStat
          label="Total"
          value={money(order.total_paisa, settings)}
          hint={`${lines.length} item${lines.length === 1 ? '' : 's'}${vat ? ' · VAT included' : ''}`}
        />
        {when ? (
          <BandStat
            label={order.valid_until ? 'Valid until' : 'Expected'}
            value={<span title={dateLabel(when, settings)}>{dateShort(when, settings)}</span>}
            meter={
              counting &&
              time && (
                <Meter
                  pct={time.pct}
                  tone={time.tone === 'neutral' ? 'primary' : time.tone}
                  label={`${time.pct}% of the time gone`}
                />
              )
            }
            hint={
              counting && time ? (
                <span
                  className={cx(
                    'font-semibold',
                    time.tone === 'danger' && 'text-danger',
                    time.tone === 'warning' && 'text-warning',
                  )}
                >
                  {time.text}
                </span>
              ) : (
                `From ${dateShort(order.doc_date, settings)}`
              )
            }
          />
        ) : (
          <BandStat label="Date" value={dateShort(order.doc_date, settings)} />
        )}
        <PartyCard
          label="Customer"
          area="people"
          name={customer === undefined && customerId ? null : (order.customer_name ?? 'Customer')}
          to={customerId ? `/customers/${customerId}` : null}
          lines={[
            [customer?.row?.phone, order.customer_pan && `PAN ${order.customer_pan}`]
              .filter(Boolean)
              .join(' · '),
            customer?.standing &&
              (customer.standing.owedPaisa > 0
                ? `Owes ${money(customer.standing.owedPaisa, settings)} now`
                : 'Owes nothing now'),
          ]}
        />
        <BandStat
          label="Billed"
          value={`${qty(billedMilli)} of ${qty(billing.orderedMilli)}`}
          meter={
            <Meter
              pct={billing.orderedMilli ? (billedMilli / billing.orderedMilli) * 100 : 0}
              tone={
                billing.progress === 'part'
                  ? 'warning'
                  : billing.progress === 'none'
                    ? 'primary'
                    : 'success'
              }
              label={`${qty(billedMilli)} of ${qty(billing.orderedMilli)} billed`}
            />
          }
          hint={
            billing.progress === 'none'
              ? 'Nothing billed yet'
              : billing.progress === 'part'
                ? `${qty(billing.orderedMilli - billedMilli)} still to bill`
                : billing.progress === 'over'
                  ? 'More billed than ordered'
                  : 'Everything is billed'
          }
        />
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Card className="p-0">
          <Table stack>
            <thead>
              <tr>
                <TH>#</TH>
                <TH>Item</TH>
                <TH align="right">Qty</TH>
                {billedAny && <TH align="right">Billed</TH>}
                <TH align="right">Rate</TH>
                <TH align="right">Discount</TH>
                <TH align="right">Amount</TH>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.id}>
                  <TD className="text-muted max-sm:hidden">{l.line_no}</TD>
                  <TD className="stack-main stack-r1">
                    {l.product_id ? (
                      <Link
                        to={`/products/${l.product_id}`}
                        className="font-medium text-ink hover:text-primary hover:underline"
                      >
                        {l.description}
                      </Link>
                    ) : (
                      <>
                        {l.description} <Badge className="ml-1 align-middle">Custom</Badge>
                      </>
                    )}
                    <div className="text-xs text-muted">
                      {lineVatLabel(registrationOf(settings), l.tax_rate_bp)}
                    </div>
                  </TD>
                  <TD
                    align="right"
                    className="stack-start stack-r2 max-sm:text-left max-sm:text-muted max-sm:before:content-['Qty_']"
                  >
                    {qty(l.qty_milli)}
                    {billedAny && (
                      <span className="sm:hidden"> · billed {qty(invoicedByLine[l.id] ?? 0)}</span>
                    )}
                  </TD>
                  {billedAny && (
                    <TD
                      align="right"
                      className={cx(
                        'max-sm:hidden',
                        (invoicedByLine[l.id] ?? 0) > l.qty_milli
                          ? 'font-medium text-warning'
                          : 'text-muted',
                      )}
                    >
                      {qty(invoicedByLine[l.id] ?? 0)}
                    </TD>
                  )}
                  <TD
                    align="right"
                    className="stack-mid stack-r2 max-sm:text-muted max-sm:before:mr-0.5 max-sm:before:content-['@']"
                  >
                    {money(l.unit_price_paisa, settings)}
                  </TD>
                  <TD
                    align="right"
                    className={cx(
                      'stack-end stack-r2 max-sm:text-muted',
                      !l.discount_paisa && 'max-sm:hidden',
                    )}
                  >
                    {l.discount_paisa ? (
                      <>
                        <span className="sm:hidden">less </span>
                        {money(l.discount_paisa, settings)}
                      </>
                    ) : (
                      ''
                    )}
                  </TD>
                  <TD align="right" className="stack-end stack-r1 max-sm:font-medium">
                    {money(l.total_paisa, settings)}
                  </TD>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>

        <div className="space-y-4">
          {steps && (
            <Card>
              <h3 className="mb-1 text-sm font-semibold">Where it stands</h3>
              <ol>
                {steps.map((step) => (
                  <Step
                    key={step.key}
                    icon={STEP_ICON[step.key]}
                    state={step.state}
                    title={step.title}
                    note={step.note}
                  >
                    {step.key === 'answer' && order.status === 'sent' && canEdit && (
                      <span className="mt-2 grid grid-cols-2 gap-2 sm:flex">
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={<Check className="size-4" />}
                          disabled={busy}
                          onClick={() => void move('accepted', 'Marked as accepted')}
                        >
                          Accepted
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={<X className="size-4" />}
                          disabled={busy}
                          onClick={() => void move('declined', 'Marked as declined')}
                        >
                          Declined
                        </Button>
                      </span>
                    )}
                  </Step>
                ))}
              </ol>
            </Card>
          )}
          <Card>
            <dl className="space-y-1.5 text-sm">
              <Line label="Subtotal" value={money(order.subtotal_paisa, settings)} />
              {order.discount_paisa > 0 && (
                <Line label="Discount" value={`−${money(order.discount_paisa, settings)}`} />
              )}
              {showsVat(registrationOf(settings), order.tax_paisa) && (
                <>
                  <Line label="Taxable" value={money(order.taxable_paisa, settings)} />
                  <Line label="VAT" value={money(order.tax_paisa, settings)} />
                </>
              )}
              {order.round_off_paisa !== 0 && (
                <Line label="Round off" value={money(order.round_off_paisa, settings)} />
              )}
              <div className="flex justify-between border-t border-border pt-2 text-base font-semibold">
                <dt>Total</dt>
                <dd className="tabular">{money(order.total_paisa, settings)}</dd>
              </div>
            </dl>
            <p className="mt-2 text-xs text-muted">{amountInWords(order.total_paisa)}</p>
            {state.label === 'Expired' && (
              <p className="mt-3 rounded-xl bg-warning-light p-2 text-sm text-warning">
                The prices were held until {dateLabel(order.valid_until, settings)}. Revise it to
                offer them again.
              </p>
            )}
            {order.notes && (
              <p className="mt-3 rounded-xl bg-soft p-2 text-sm">
                <span className="text-muted">Note: </span>
                {order.notes}
              </p>
            )}
            {(order.status === 'cancelled' || order.status === 'closed') && (
              <p
                className={cx(
                  'mt-3 rounded-xl p-2 text-sm',
                  order.status === 'cancelled' ? 'bg-danger-light text-danger' : 'bg-soft',
                )}
              >
                {order.status === 'cancelled' ? 'Cancelled' : 'Closed'}: {order.close_reason}
              </p>
            )}
          </Card>

          {(related?.source || (related?.later.length ?? 0) > 0) && (
            <Card>
              <h3 className="mb-2 text-sm font-semibold">Related</h3>
              <ul className="space-y-1 text-sm">
                {related?.source && (
                  <li>
                    <span className="text-muted">
                      {related.source.kind === kind ? 'Revises ' : 'Made from '}
                    </span>
                    <DocLink order={related.source} />
                  </li>
                )}
                {related?.later.map((o) => (
                  <li key={o.id}>
                    <span className="text-muted">
                      {o.kind === kind ? 'Revised as ' : 'Became '}
                    </span>
                    <DocLink order={o} />
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {invoices.length > 0 && (
            <Card>
              <h3 className="mb-2 text-sm font-semibold">Bills made from it</h3>
              <ul className="space-y-1.5 text-sm">
                {invoices.map((inv) => (
                  <li key={inv.id} className="flex items-center justify-between gap-3">
                    <Link
                      to={`/invoices/${inv.id}`}
                      className="min-w-0 truncate font-medium hover:text-primary hover:underline"
                    >
                      {inv.number ?? (inv.status === 'draft' ? 'Draft invoice' : 'Number on sync')}
                    </Link>
                    <span className="flex shrink-0 items-center gap-2">
                      <StatusBadge invoice={inv} />
                      <span className="tabular">{money(inv.total_paisa, settings)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {(order.terms ?? (kind === 'quotation' ? settings?.quote_terms : null)) && (
            <Card>
              <h3 className="mb-2 text-sm font-semibold">Terms</h3>
              <p className="whitespace-pre-line text-sm text-muted">
                {order.terms ?? settings?.quote_terms}
              </p>
            </Card>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={ask === 'cancelled' || ask === 'closed'}
        title={ask === 'closed' ? `Close this ${noun}?` : `Cancel this ${noun}?`}
        message={
          ask === 'closed'
            ? 'Nothing more will be billed against it. Bills already made stay as they are.'
            : `It keeps its number and stays in the list as cancelled.`
        }
        confirmLabel={ask === 'closed' ? `Close ${noun}` : `Cancel ${noun}`}
        danger={ask === 'cancelled'}
        busy={busy}
        onClose={() => setAsk(null)}
        onConfirm={() => {
          if (!reason.trim()) {
            toast.error('Write a short reason first.')
            return
          }
          if (ask === 'cancelled' || ask === 'closed')
            void move(
              ask,
              ask === 'closed' ? `${words.one} closed` : `${words.one} cancelled`,
              reason,
            )
        }}
      >
        <Field label="Reason">
          {(fieldId) => (
            <Textarea
              id={fieldId}
              rows={2}
              maxLength={300}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
        </Field>
      </ConfirmDialog>
      <ConfirmDialog
        open={ask === 'delete'}
        title="Delete this draft?"
        message="A draft has no number, so nothing is lost but what you typed."
        confirmLabel="Delete draft"
        danger
        busy={busy}
        onClose={() => setAsk(null)}
        onConfirm={() =>
          void run(
            () => [
              { table: 'sales_orders', op: 'delete', row: { id: order.id } },
              ...lines.map((l): SyncOp => ({
                table: 'sales_order_lines',
                op: 'delete',
                row: { id: l.id },
              })),
            ],
            'Draft deleted',
          ).then((done) => done && navigate(words.path))
        }
      />
      {printing && <DocPrintDialog {...printing} onClose={() => setPrinting(null)} />}
    </div>
  )
}

const STEP_ICON: Record<QuotationStep['key'], ReactNode> = {
  saved: <CheckCircle2 className="size-4" />,
  sent: <Send className="size-4" />,
  answer: <MessageSquare className="size-4" />,
  next: <ReceiptIcon className="size-4" />,
}

function DocLink({ order }: { order: Pick<SalesOrderRow, 'id' | 'kind' | 'number' | 'status'> }) {
  return (
    <Link to={salesOrderPath(order)} className="font-medium hover:text-primary hover:underline">
      {order.number ?? `${KIND_WORDS[order.kind].one} (${order.status})`}
    </Link>
  )
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted">{label}</dt>
      <dd className="tabular">{value}</dd>
    </div>
  )
}

/** A customer's quotations or orders, newest first (the customer page). */
export function CustomerSalesOrders({
  customerId,
  kind,
}: {
  customerId: string
  kind: SalesOrderKind
}) {
  const settings = useSettings()
  const rows = useLive(
    (repo) => repo.salesOrders.list({ kind, customerId, limit: 10 }),
    [kind, customerId],
    ['sales_orders'],
  ).data?.rows
  const today = todayIso()
  if (!rows?.length) return null
  return (
    <Card>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{KIND_WORDS[kind].many}</h3>
        <FileText className="size-4 text-muted" aria-hidden />
      </div>
      <ul className="space-y-1.5 text-sm">
        {rows.map((o) => {
          const state = salesOrderState(o, today)
          return (
            <li key={o.id} className="flex items-center justify-between gap-3">
              <span className="min-w-0 truncate">
                <DocLink order={o} />
                <span className="ml-2 text-muted" title={dateLabel(o.doc_date, settings)}>
                  {dateShort(o.doc_date, settings)}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <StateSwap state={state.label}>
                  <Badge tone={state.tone}>{state.label}</Badge>
                </StateSwap>
                <span className="tabular">{money(o.total_paisa, settings)}</span>
              </span>
            </li>
          )
        })}
      </ul>
    </Card>
  )
}
